import { useState } from "react";
import { ErrorMessage, FormikProvider, useFormik } from "formik";
import {
  getValidationSchema,
  initialValues as initialValuesBase,
} from "./config";
import { Box } from "@mui/material";
import { StyledFormicError } from "./styles";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid2";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import { green } from "@mui/material/colors";
import {
  useAddWellnessResourceMutation,
  useUpdateWellnessResourceMutation,
} from "graphql/__generated__/hooks";
import { Modal } from "components/Modal";
import { WellnessResource } from "types/wellnessResource";
import { useNotifications } from "hooks/useNotifications";
import { Messages, NotificationSeverity } from "helpers/constants";

export type Props = {
  createOrEditWellnessResourceModal: boolean;
  handleCreateOrEditWellnessResourceModalDismiss: () => void;
  handleCreateOrEditWellnessResourceChange: (
    resource?: WellnessResource,
  ) => string | void;
  wellnessResource?: WellnessResource | null;
};

const CreateOrEditWellnessResourceModal = ({
  createOrEditWellnessResourceModal,
  handleCreateOrEditWellnessResourceModalDismiss,
  handleCreateOrEditWellnessResourceChange,
  wellnessResource = null,
}: Props) => {
  const isEditMode = wellnessResource !== null;
  const [loading, setLoading] = useState(false);
  const [addResource] = useAddWellnessResourceMutation();
  const [updateResource] = useUpdateWellnessResourceMutation();
  const { notify } = useNotifications();

  const handleCreateOrEditWellnessResource = async (
    reqValues: WellnessResource,
  ) => {
    setLoading(true);

    await addResource({
      variables: reqValues,
    });
    handleCreateOrEditWellnessResourceChange();
    setLoading(false);
    notify({
      message: Messages.successResourceCreated,
      severity: NotificationSeverity.success,
    });
  };

  const handleEditResource = async (reqValues: WellnessResource) => {
    setLoading(true);

    await updateResource({
      variables: {
        id: wellnessResource?.wellness_resource_id || "",
        wellness_resource: reqValues,
      },
    });
    handleCreateOrEditWellnessResourceChange();
    setLoading(false);
    notify({
      message: Messages.successResourceUpdated,
      severity: NotificationSeverity.success,
    });
  };

  const initialValues = isEditMode
    ? {
        resourceName: wellnessResource?.name || "",
        resourceDescription: wellnessResource?.description || "",
        resourceLink: wellnessResource?.link || "",
      }
    : initialValuesBase;

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: getValidationSchema,
    enableReinitialize: true,
    onSubmit: (values) => {
      const reqValues = {
        name: values.resourceName,
        description: values.resourceDescription,
        link: values.resourceLink,
      };
      if (isEditMode) {
        handleEditResource(reqValues as WellnessResource).then();
      } else {
        handleCreateOrEditWellnessResource(
          reqValues as WellnessResource,
        ).then();
      }
    },
  });

  return (
    <Modal
      open={createOrEditWellnessResourceModal}
      onClose={handleCreateOrEditWellnessResourceModalDismiss}
      title={
        isEditMode ? wellnessResource?.name : "Create a new Wellness Resource"
      }
      size="small"
    >
      <FormikProvider value={formik}>
        <form onSubmit={formik.handleSubmit}>
          <Box sx={{ mt: 1 }}>
            <Box>
              <TextField
                variant="standard"
                onChange={formik.handleChange}
                margin="dense"
                fullWidth
                name="resourceName"
                label="Wellness Resource Name"
                type="text"
                id="resourceName"
                value={formik?.values?.resourceName}
              />
              <ErrorMessage name="resourceName" component={StyledFormicError} />
            </Box>

            <Box>
              <TextField
                variant="standard"
                onChange={formik.handleChange}
                margin="dense"
                fullWidth
                name="resourceDescription"
                label="Resource Description"
                type="text"
                id="resourceName"
                value={formik?.values?.resourceDescription}
              />
              <ErrorMessage
                name="resourceDescription"
                component={StyledFormicError}
              />
            </Box>

            <Box>
              <TextField
                variant="standard"
                onChange={formik.handleChange}
                margin="dense"
                fullWidth
                name="resourceLink"
                label="Resource Link"
                type="text"
                id="resourceLink"
                value={formik?.values?.resourceLink}
              />
              <ErrorMessage name="resourceLink" component={StyledFormicError} />
            </Box>

            <Grid sx={{ mt: 5, display: "flex", justifyContent: "flex-end" }}>
              <Box mr={2}>
                <Button
                  variant="outlined"
                  onClick={handleCreateOrEditWellnessResourceModalDismiss}
                >
                  Cancel
                </Button>
              </Box>
              <Box>
                <Button
                  disabled={loading}
                  type="submit"
                  variant="contained"
                  color="primary"
                >
                  Submit
                </Button>
              </Box>
              {loading && (
                <CircularProgress
                  size={30}
                  sx={{
                    color: green[500],
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    marginTop: "-12px",
                    marginLeft: "-12px",
                  }}
                />
              )}
            </Grid>
          </Box>
        </form>
      </FormikProvider>
    </Modal>
  );
};

export default CreateOrEditWellnessResourceModal;
